/**
 * Tenant-side Rider Ledger page.
 *
 * Lists every in-app rider in the tenant with their balance owed, click to
 * drill into a per-rider ledger view, and an inline modal to record
 * payouts/adjustments + edit the per-rider payout override or the tenant
 * default. BizilGO drivers are intentionally not listed — they're paid by
 * BizilGO directly.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, Wallet, IndianRupee, ArrowLeft, Plus, Settings, BadgeIndianRupee, Camera, Check, X, Clock, RefreshCcw } from "lucide-react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import AdminSearchBar from "../../components/AdminSearchBar";

const METHODS = ["CASH", "UPI", "BANK", "OTHER"];

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}

export default function AdminRiderLedger() {
    const [list, setList] = useState({ rider_payout_per_delivery: 0, rows: [] });
    const [query, setQuery] = useState("");
    const [activeRider, setActiveRider] = useState(null);   // {rider, summary, entries}
    const [busy, setBusy] = useState(false);
    const [showTenantCfg, setShowTenantCfg] = useState(false);
    const [tenantPayout, setTenantPayout] = useState(0);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/rider-ledger");
            setList(data);
            setTenantPayout(data.rider_payout_per_delivery || 0);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return list.rows;
        return list.rows.filter((r) => `${r.name || ""} ${r.mobile_number || ""}`.toLowerCase().includes(q));
    }, [list.rows, query]);

    const openRider = async (rider_id) => {
        try {
            const { data } = await api.get(`/admin/rider-ledger/${rider_id}`);
            setActiveRider(data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to open rider ledger"); }
    };

    const saveTenantPayout = async () => {
        setBusy(true);
        try {
            await api.patch("/admin/rider-payout-config", { rider_payout_per_delivery: Number(tenantPayout) });
            toast.success("Tenant default updated");
            setShowTenantCfg(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-rider-ledger-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Rider Ledger</h1>
                    <p className="text-sm text-muted-foreground mt-1">{list.rows.length} riders · Riders earn each order's <strong>delivery fee</strong> by default · fallback {inr(list.rider_payout_per_delivery || 0)}/drop</p>
                </div>
                <button
                    data-testid="open-tenant-payout-cfg"
                    onClick={() => setShowTenantCfg(true)}
                    className="bg-card border border-border rounded-xl px-3 py-2 font-semibold text-sm inline-flex items-center gap-2"
                ><Settings size={14} /> Fallback payout</button>
            </div>

            <div className="mt-4">
                <AdminSearchBar value={query} onChange={setQuery} placeholder="Search by name or mobile…" testid="rider-ledger-search" count={filtered.length} />
            </div>

            <PendingDepositsPanel onChanged={load} />

            <div className="mt-4 bg-card border border-border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                    <thead><tr className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                        <th className="px-4 py-3">Rider</th>
                        <th className="px-4 py-3">Payout/Drop</th>
                        <th className="px-4 py-3">Deliveries</th>
                        <th className="px-4 py-3">Earned</th>
                        <th className="px-4 py-3">Paid</th>
                        <th className="px-4 py-3">Balance owed</th>
                        <th className="px-4 py-3"></th>
                    </tr></thead>
                    <tbody>
                        {filtered.map((r) => (
                            <tr key={r.rider_id} data-testid={`rider-row-${r.rider_id}`} className="border-t border-border">
                                <td className="px-4 py-3">
                                    <p className="font-semibold">{r.name || "—"}</p>
                                    <p className="text-xs text-muted-foreground">+91 {r.mobile_number} · {r.vehicle_type}</p>
                                </td>
                                <td className="px-4 py-3 text-xs">
                                    {r.payout_per_delivery != null
                                        ? <span className="font-bold text-emerald-700">{inr(r.payout_per_delivery)} <span className="text-muted-foreground font-normal">(override)</span></span>
                                        : <span className="text-muted-foreground">Delivery fee <span className="text-[10px]">(fallback {inr(list.rider_payout_per_delivery || 0)})</span></span>}
                                </td>
                                <td className="px-4 py-3 font-bold">{r.deliveries}</td>
                                <td className="px-4 py-3 text-emerald-700 font-bold">{inr(r.total_earned)}</td>
                                <td className="px-4 py-3 text-sky-700 font-bold">{inr(r.total_paid)}</td>
                                <td className="px-4 py-3 font-black">{inr(r.balance)}</td>
                                <td className="px-4 py-3 text-right">
                                    <button data-testid={`open-rider-${r.rider_id}`} onClick={() => openRider(r.rider_id)} className="text-xs text-primary font-bold">Open ledger →</button>
                                </td>
                            </tr>
                        ))}
                        {list.rows.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-muted-foreground"><Wallet size={28} className="inline opacity-40" /><p className="mt-2">No riders yet. Add one from the Riders page.</p></td></tr>}
                    </tbody>
                </table>
            </div>

            {activeRider && <RiderLedgerDrawer data={activeRider} onClose={() => setActiveRider(null)} onAction={() => { openRider(activeRider.rider.id); load(); }} tenantDefault={list.rider_payout_per_delivery} />}

            {showTenantCfg && (
                <Modal title="Fallback payout per delivery" onClose={() => setShowTenantCfg(false)}>
                    <p className="text-xs text-muted-foreground mb-3">Used <strong>only</strong> when the order has no delivery fee (e.g. pickup orders) and the rider has no per-rider override. Normally riders earn each order's zone-computed delivery fee.</p>
                    <label className="block">
                        <span className="text-xs font-semibold text-muted-foreground">Amount (₹)</span>
                        <input data-testid="tenant-payout-input" type="number" min={0} step="0.5" className="inp" value={tenantPayout} onChange={(e) => setTenantPayout(e.target.value)} />
                    </label>
                    <button data-testid="save-tenant-payout" onClick={saveTenantPayout} disabled={busy} className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy && <Loader2 size={14} className="animate-spin" />} Save fallback
                    </button>
                </Modal>
            )}
            <style>{`.inp{display:block;background:hsl(var(--muted));border-radius:0.75rem;padding:0.6rem 0.75rem;outline:none;font-size:0.875rem;width:100%;margin-top:4px}`}</style>
        </div>
    );
}

function RiderLedgerDrawer({ data, onClose, onAction, tenantDefault }) {
    const [mode, setMode] = useState(null);  // 'payout' | 'adjustment' | 'override'
    const [payout, setPayout] = useState({ amount: "", payment_method: "UPI", note: "" });
    const [adj, setAdj] = useState({ amount: "", note: "" });
    const [override, setOverride] = useState(data.rider.payout_per_delivery ?? "");
    const [busy, setBusy] = useState(false);

    const submitPayout = async () => {
        if (!payout.amount || Number(payout.amount) <= 0) return toast.error("Amount must be positive");
        setBusy(true);
        try {
            await api.post(`/admin/rider-ledger/${data.rider.id}/payout`, {
                amount: Number(payout.amount), payment_method: payout.payment_method, note: payout.note,
            });
            toast.success("Payout recorded");
            setMode(null); setPayout({ amount: "", payment_method: "UPI", note: "" });
            onAction();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const submitAdjustment = async () => {
        if (!adj.amount || Number(adj.amount) === 0) return toast.error("Amount cannot be zero");
        setBusy(true);
        try {
            await api.post(`/admin/rider-ledger/${data.rider.id}/adjustment`, {
                amount: Number(adj.amount), note: adj.note,
            });
            toast.success("Adjustment posted");
            setMode(null); setAdj({ amount: "", note: "" });
            onAction();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const submitOverride = async () => {
        setBusy(true);
        try {
            const payload = override === "" ? { payout_per_delivery: null } : { payout_per_delivery: Number(override) };
            await api.patch(`/admin/riders/${data.rider.id}/payout-config`, payload);
            toast.success(override === "" ? "Override cleared" : "Override saved");
            setMode(null);
            onAction();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="rider-ledger-drawer" className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
            <div className="w-full sm:max-w-lg h-full bg-background overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-background border-b border-border px-5 py-3 flex items-center gap-3">
                    <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg" data-testid="close-rider-drawer"><ArrowLeft size={18} /></button>
                    <div>
                        <p className="font-bold">{data.rider.name}</p>
                        <p className="text-xs text-muted-foreground">+91 {data.rider.mobile_number}</p>
                    </div>
                </div>

                <div className="px-5 py-4">
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="bg-emerald-50 border border-emerald-100 rounded-xl py-3"><p className="text-emerald-700 font-black text-base">{inr(data.summary.total_earned)}</p><p className="text-emerald-700/70 uppercase tracking-wider">Earned</p></div>
                        <div className="bg-sky-50 border border-sky-100 rounded-xl py-3"><p className="text-sky-700 font-black text-base">{inr(data.summary.total_paid)}</p><p className="text-sky-700/70 uppercase tracking-wider">Paid</p></div>
                        <div className="bg-amber-50 border border-amber-100 rounded-xl py-3"><p className="text-amber-700 font-black text-base">{inr(data.summary.balance)}</p><p className="text-amber-700/70 uppercase tracking-wider">Balance</p></div>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2 text-center inline-flex items-center justify-center gap-1 w-full">
                        <BadgeIndianRupee size={11} />
                        {data.rider.payout_per_delivery != null
                            ? <>{inr(data.rider.payout_per_delivery)}/drop (override)</>
                            : <>Earns the order's delivery fee · zone-based · fallback {inr(tenantDefault)}</>
                        } · {data.summary.deliveries} deliveries
                    </p>

                    <div className="mt-4 grid grid-cols-3 gap-2 text-xs font-bold">
                        <button data-testid="rider-action-payout" onClick={() => setMode("payout")} className="bg-sky-600 text-white rounded-xl py-2 inline-flex items-center justify-center gap-1"><Plus size={12} /> Payout</button>
                        <button data-testid="rider-action-adjustment" onClick={() => setMode("adjustment")} className="bg-amber-500 text-amber-950 rounded-xl py-2 inline-flex items-center justify-center gap-1"><IndianRupee size={12} /> Adjustment</button>
                        <button data-testid="rider-action-override" onClick={() => setMode("override")} className="bg-muted text-foreground border border-border rounded-xl py-2 inline-flex items-center justify-center gap-1"><Settings size={12} /> Override</button>
                    </div>

                    {mode === "payout" && (
                        <Section title="Record payout">
                            <div className="grid grid-cols-2 gap-3">
                                <label><span className="text-xs font-semibold text-muted-foreground">Amount (₹)</span>
                                    <input data-testid="payout-amount" type="number" min={0} step="0.5" className="inp" value={payout.amount} onChange={(e) => setPayout({ ...payout, amount: e.target.value })} />
                                </label>
                                <label><span className="text-xs font-semibold text-muted-foreground">Method</span>
                                    <select data-testid="payout-method" className="inp" value={payout.payment_method} onChange={(e) => setPayout({ ...payout, payment_method: e.target.value })}>
                                        {METHODS.map((m) => <option key={m}>{m}</option>)}
                                    </select>
                                </label>
                                <label className="col-span-2"><span className="text-xs font-semibold text-muted-foreground">Note (optional)</span>
                                    <input data-testid="payout-note" className="inp" value={payout.note} onChange={(e) => setPayout({ ...payout, note: e.target.value })} placeholder="e.g. weekly settlement" />
                                </label>
                            </div>
                            <button data-testid="save-payout" disabled={busy} onClick={submitPayout} className="mt-3 w-full bg-sky-600 text-white rounded-xl py-3 font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Save payout</button>
                        </Section>
                    )}

                    {mode === "adjustment" && (
                        <Section title="Record adjustment">
                            <p className="text-xs text-muted-foreground mb-2">Positive credits the rider, negative debits.</p>
                            <div className="grid grid-cols-1 gap-3">
                                <label><span className="text-xs font-semibold text-muted-foreground">Signed amount (₹)</span>
                                    <input data-testid="adjustment-amount" type="number" step="0.5" className="inp" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} placeholder="e.g. -50 for late fine" />
                                </label>
                                <label><span className="text-xs font-semibold text-muted-foreground">Reason</span>
                                    <input data-testid="adjustment-note" className="inp" value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} placeholder="Fuel reimbursement / fine / …" />
                                </label>
                            </div>
                            <button data-testid="save-adjustment" disabled={busy} onClick={submitAdjustment} className="mt-3 w-full bg-amber-500 text-amber-950 rounded-xl py-3 font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Save adjustment</button>
                        </Section>
                    )}

                    {mode === "override" && (
                        <Section title="Per-rider payout override">
                            <p className="text-xs text-muted-foreground mb-2">When set, the rider earns this flat amount per delivery instead of the order's delivery fee. Leave blank to revert to the delivery-fee pass-through (with fallback {inr(tenantDefault)}).</p>
                            <input data-testid="override-amount" type="number" min={0} step="0.5" className="inp" value={override} onChange={(e) => setOverride(e.target.value)} placeholder="e.g. 50" />
                            <button data-testid="save-override" disabled={busy} onClick={submitOverride} className="mt-3 w-full bg-foreground text-background rounded-xl py-3 font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Save override</button>
                        </Section>
                    )}

                    <h3 className="font-bold mt-6 mb-2 text-sm">Ledger ({data.entries.length})</h3>
                    <div className="space-y-2">
                        {data.entries.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No entries yet.</p>}
                        {data.entries.map((e) => {
                            const negative = e.kind === "PAYOUT" || (e.kind === "ADJUSTMENT" && e.amount < 0);
                            return (
                                <div key={e.id} data-testid={`drawer-row-${e.id}`} className="bg-card border border-border rounded-xl px-3 py-2 flex items-center gap-2 text-sm">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{e.kind}</span>
                                            {e.order_unique_id && <span className="text-[11px] font-bold text-muted-foreground">#{e.order_unique_id}</span>}
                                            {e.payment_method && <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">via {e.payment_method}</span>}
                                        </div>
                                        {e.note && <p className="text-xs mt-0.5 truncate">{e.note}</p>}
                                        <p className="text-[11px] text-muted-foreground">{fmtDate(e.created_at)}</p>
                                    </div>
                                    <p className={`font-black text-sm ${negative ? "text-rose-600" : "text-emerald-700"}`}>{negative ? "−" : "+"}{inr(Math.abs(e.amount))}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="mt-4 bg-card border border-border rounded-2xl p-4">
            <h3 className="font-bold text-sm mb-3">{title}</h3>
            {children}
        </div>
    );
}

function Modal({ title, children, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={onClose}>
            <div className="bg-background rounded-2xl shadow-xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-bold text-lg mb-3">{title}</h3>
                {children}
            </div>
        </div>
    );
}

/**
 * Pending Rider Deposits — admin queue for ``COD_REMIT_PENDING`` ledger
 * rows submitted by riders. Approve posts a canonical ``COD_REMITTED``
 * row and flips the pending entry's ``confirmed_at``; Reject requires
 * a non-empty reason which is persisted + surfaced to the rider.
 */
function PendingDepositsPanel({ onChanged }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [zoom, setZoom] = useState(null);            // photo url for lightbox
    const [rejectTarget, setRejectTarget] = useState(null); // pending row being rejected

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/rider-ledger/pending-deposits");
            setRows(data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load pending deposits");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const approve = async (row) => {
        try {
            await api.post(`/admin/rider-ledger/${row.rider_id}/cod-receive`, {
                amount: Number(row.amount),
                pending_id: row.id,
                note: `Approved deposit (pending #${row.id.slice(0, 8)})`,
            });
            toast.success(`Approved ₹${row.amount}`);
            load();
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to approve");
        }
    };

    if (!loading && rows.length === 0) return null;

    return (
        <div className="mt-4 bg-card border border-border rounded-2xl p-4" data-testid="admin-pending-deposits-panel">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Clock size={16} className="text-amber-600" />
                    <h2 className="font-bold text-base">Pending Deposits</h2>
                    <span className="text-xs bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full" data-testid="pending-deposits-count">{rows.length}</span>
                </div>
                <button
                    onClick={load}
                    className="text-xs font-bold text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
                    data-testid="pending-deposits-refresh"
                >
                    <RefreshCcw size={12} /> Refresh
                </button>
            </div>

            {loading ? (
                <p className="text-center text-sm text-muted-foreground py-6"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</p>
            ) : (
                <div className="space-y-2">
                    {rows.map((r) => (
                        <div
                            key={r.id}
                            data-testid={`pending-deposit-${r.id}`}
                            className="border border-border rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3 bg-muted/30"
                        >
                            {r.photo_url ? (
                                <button
                                    type="button"
                                    onClick={() => setZoom(r.photo_url)}
                                    className="shrink-0"
                                    data-testid={`pending-deposit-photo-${r.id}`}
                                >
                                    <img src={r.photo_url} alt="deposit evidence" className="w-20 h-20 object-cover rounded-lg border border-border" />
                                </button>
                            ) : (
                                <div
                                    className="shrink-0 w-20 h-20 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground"
                                    data-testid={`pending-deposit-nophoto-${r.id}`}
                                >
                                    <Camera size={20} className="opacity-40" />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                    <span className="font-black text-lg" data-testid={`pending-deposit-amount-${r.id}`}>{inr(r.amount)}</span>
                                    <span className="text-xs text-muted-foreground">by</span>
                                    <span className="font-semibold text-sm">{r.rider_name || "—"}</span>
                                    {r.rider_mobile && <span className="text-xs text-muted-foreground">+91 {r.rider_mobile}</span>}
                                </div>
                                {r.note && <p className="text-xs mt-1 text-muted-foreground truncate">{r.note}</p>}
                                <p className="text-[11px] text-muted-foreground mt-0.5">{fmtDate(r.created_at)}</p>
                            </div>
                            <div className="flex gap-2 sm:flex-col">
                                <button
                                    onClick={() => approve(r)}
                                    data-testid={`pending-deposit-approve-${r.id}`}
                                    className="bg-emerald-600 text-white rounded-lg px-3 py-2 text-xs font-bold inline-flex items-center gap-1"
                                >
                                    <Check size={12} /> Approve
                                </button>
                                <button
                                    onClick={() => setRejectTarget(r)}
                                    data-testid={`pending-deposit-reject-${r.id}`}
                                    className="bg-rose-600 text-white rounded-lg px-3 py-2 text-xs font-bold inline-flex items-center gap-1"
                                >
                                    <X size={12} /> Reject
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {zoom && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
                    onClick={() => setZoom(null)}
                    data-testid="deposit-photo-lightbox"
                >
                    <img src={zoom} alt="deposit evidence" className="max-h-full max-w-full rounded-xl" />
                </div>
            )}

            {rejectTarget && (
                <RejectDepositModal
                    row={rejectTarget}
                    onClose={() => setRejectTarget(null)}
                    onDone={() => { setRejectTarget(null); load(); }}
                />
            )}
        </div>
    );
}

function RejectDepositModal({ row, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        const r = reason.trim();
        if (!r) return toast.error("Reason is required");
        setBusy(true);
        try {
            await api.post(`/admin/rider-ledger/${row.rider_id}/cod-deposit-reject`, {
                pending_id: row.id,
                reason: r,
            });
            toast.success("Deposit rejected");
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to reject");
        } finally { setBusy(false); }
    };
    return (
        <Modal title={`Reject ${inr(row.amount)} deposit`} onClose={onClose}>
            <p className="text-xs text-muted-foreground mb-3">
                The rider will see this reason on their ledger so they can re-submit. Be specific (e.g. &quot;Photo unclear, please retake&quot;).
            </p>
            <label className="block">
                <span className="text-xs font-semibold text-muted-foreground">Reason</span>
                <textarea
                    data-testid="reject-deposit-reason"
                    className="inp"
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Cash count short by ₹50, please re-submit corrected amount"
                />
            </label>
            <button
                data-testid="reject-deposit-submit"
                disabled={busy || !reason.trim()}
                onClick={submit}
                className="mt-4 w-full bg-rose-600 text-white rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
                {busy && <Loader2 size={14} className="animate-spin" />} Reject deposit
            </button>
        </Modal>
    );
}
